import { useEffect, useRef, useState } from 'react'
import { DocxExportNoticePhase, type DocxExportNotice } from '@/agent-v2/docx-export/schemas'
import type { AgentV2Message } from '@/agent-v2/store/reducer'

/**
 * The chat's one live region for exports: tells a screen reader when an export this chat showed in
 * progress ends. Results already settled when they first appear, such as loaded history, stay quiet.
 */
export function DocxExportAnnouncer({ messages }: { messages: AgentV2Message[] }) {
  // Exports seen in progress while mounted; each is announced once, when its result arrives.
  const inProgressIdsRef = useRef(new Set<string>())
  const [announcement, setAnnouncement] = useState<{ exportId: string; text: string } | null>(null)

  useEffect(() => {
    const notices = messages.flatMap((message) => (message.docxExport ? [message.docxExport] : []))
    const settledIds = new Set(notices.filter((notice) => notice.phase !== DocxExportNoticePhase.STARTED).map((notice) => notice.export_id))
    let latestSettled: DocxExportNotice | undefined
    for (const notice of notices) {
      if (notice.phase === DocxExportNoticePhase.STARTED) {
        if (!settledIds.has(notice.export_id)) inProgressIdsRef.current.add(notice.export_id)
      } else if (inProgressIdsRef.current.delete(notice.export_id)) {
        latestSettled = notice
      }
    }
    if (latestSettled) setAnnouncement({ exportId: latestSettled.export_id, text: announcementFor(latestSettled) })
  }, [messages])

  // Always mounted, so screen readers are already watching it when the text changes. A bare live
  // region, since the chat's "Loading earlier messages…" already holds its status role. Each result
  // is a new node: a retry that fails the same way repeats the same words, which would otherwise
  // leave the region unchanged and silent.
  return (
    <p aria-live="polite" aria-atomic="true" className="sr-only">
      {announcement && <span key={announcement.exportId}>{announcement.text}</span>}
    </p>
  )
}

/** The same words as the toast shown when the chat is not on screen. */
function announcementFor(notice: DocxExportNotice) {
  if (notice.phase === DocxExportNoticePhase.READY) return `${notice.file_name ?? `${notice.draft_title}.docx`} is ready`
  return `Couldn't export ${notice.draft_title}`
}
